import { useEffect, useState, useCallback, useRef } from "react";
import { Loader2, RefreshCw, Headphones, Volume2 } from "lucide-react";

import { AdminHeader } from "@/components/admin/AdminHeader";
import { ArtworkCard } from "@/components/admin/ArtworkCard";
import { useSingleAudioPlayer } from "@/hooks/useSingleAudioPlayer";
import { adminApi } from "@/lib/adminApi";

const REFRESH_MS = 8000;

export default function AdminDashboardPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [imgVersion, setImgVersion] = useState(0); // cache-buster pour les <img>
  const audio = useSingleAudioPlayer();
  const toastTimerRef = useRef(null);

  const showToast = useCallback((message) => {
    setToast(message);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToast(null), 2200);
  }, []);

  const fetchAll = useCallback(async ({ manual = false } = {}) => {
    if (manual) setRefreshing(true);
    try {
      const data = await adminApi.nowPlayingAll(manual ? Date.now() : undefined);
      setItems(data.items || []);
      setError(null);
      if (manual) {
        setImgVersion((v) => v + 1); // force re-fetch des <img> côté navigateur
        showToast("✓ Tableau actualisé");
      }
    } catch (e) {
      if (e.status !== 401) setError(e.message);
    } finally {
      setLoading(false);
      if (manual) setRefreshing(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchAll();
    const id = setInterval(() => fetchAll(), REFRESH_MS);
    return () => clearInterval(id);
  }, [fetchAll]);

  return (
    <div className="mw-app">
      <AdminHeader />
      <main className="admin-main">
        <div className="admin-pagehead">
          <div>
            <span className="mw-section-eyebrow">Validation des artworks</span>
            <h1 className="admin-h1">Centrale de contrôle</h1>
            <p className="admin-lead">
              Chaque pochette validée ici devient prioritaire sur iTunes / Deezer / MusicBrainz
              pour toutes vos PWAs et le mur WallTunes.
            </p>
          </div>
          <button
            type="button"
            className="mw-btn mw-btn-ghost"
            onClick={() => fetchAll({ manual: true })}
            disabled={refreshing}
            data-testid="admin-refresh"
          >
            <RefreshCw size={14} className={refreshing ? "mw-spin" : ""} />{" "}
            {refreshing ? "Actualisation…" : "Actualiser"}
          </button>
        </div>

        {loading && items.length === 0 ? (
          <div className="admin-loading">
            <Loader2 size={20} className="mw-spin" /> Récupération des flux live…
          </div>
        ) : error ? (
          <div className="mw-form-error">{error}</div>
        ) : (
          <div className="art-grid" data-testid="admin-art-grid">
            {items.map((it) => (
              <ArtworkCard
                key={it.radio.id}
                item={it}
                imgVersion={imgVersion}
                onChanged={() => fetchAll({ manual: true })}
                audio={audio}
              />
            ))}
          </div>
        )}
      </main>

      {toast && (
        <div className="admin-toast" data-testid="admin-toast">
          {toast}
        </div>
      )}

      {audio.playingId && (
        <div className="admin-mini-player" data-testid="admin-mini-player">
          <Headphones size={14} />
          <span className="admin-mp-label">En écoute · {audio.playingId}</span>
          <Volume2 size={14} />
          <input
            type="range"
            min="0"
            max="1"
            step="0.05"
            value={audio.volume}
            onChange={(e) => audio.setVolume(parseFloat(e.target.value))}
            className="admin-mp-slider"
          />
          <button type="button" className="admin-mp-stop" onClick={audio.stop}>
            Stop
          </button>
        </div>
      )}
    </div>
  );
}
